import { useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Pause } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * VoiceButton — plays a text passage in Ms. Pradhan's voice.
 * Uses POST /api/pradhan/tts (returns audio/mpeg).  Requires auth.
 * Caches the last MP3 blob per (text, voice) so replaying is instant.
 */
export default function VoiceButton({ text, label = "Listen", voice = "coral", className = "", "data-testid": testid = "voice-button" }) {
  const { user } = useAuth();
  const audioRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const cacheRef = useRef({ key: null, url: null });

  useEffect(() => () => {
    if (cacheRef.current.url) URL.revokeObjectURL(cacheRef.current.url);
  }, []);

  const key = `${voice}::${(text || "").slice(0, 200)}`;

  const play = async () => {
    if (!user) { toast.info("Sign in to hear Ms. Pradhan's voice"); return; }
    if (playing) { audioRef.current?.pause(); setPlaying(false); return; }
    if (cacheRef.current.key === key && cacheRef.current.url) {
      audioRef.current.src = cacheRef.current.url;
      audioRef.current.play(); setPlaying(true); return;
    }
    setBusy(true);
    try {
      const token = localStorage.getItem("dpa_token");
      const r = await fetch(`${API}/pradhan/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text, voice }),
      });
      if (!r.ok) throw new Error("Voice synthesis failed");
      const blob = await r.blob();
      if (cacheRef.current.url) URL.revokeObjectURL(cacheRef.current.url);
      const url = URL.createObjectURL(blob);
      cacheRef.current = { key, url };
      audioRef.current.src = url;
      await audioRef.current.play();
      setPlaying(true);
    } catch (e) {
      toast.error("Could not synthesize Ms. Pradhan's voice");
    } finally { setBusy(false); }
  };

  return (
    <>
      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        preload="none"
      />
      <button
        type="button"
        onClick={play}
        disabled={busy || !text}
        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold rounded-full border-2 border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors ${className}`}
        data-testid={testid}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : playing ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
        {busy ? "Warming up…" : playing ? "Stop" : label}
      </button>
    </>
  );
}
